import React from 'react';
import {
  X,
  Trophy,
  Sparkles,
  ArrowRight,
  Clock,
  Volume2,
  Users,
  LogOut,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import {
  SessionGameState,
  SessionPlayerState,
  SessionGameType
} from '../../../types/session-games';
import {
  submitPlayerAnswer,
  advanceSessionRound,
  cancelOrEndSessionGame,
  startSessionGame,
  GAME_OPTIONS
} from '../../../services/sessionGameService';
import { PronunciationDuelView } from './PronunciationDuelView';
import { SentenceArchitectView } from './SentenceArchitectView';
import { TranslationDuelView } from './TranslationDuelView';
import { NewsDetectiveView } from './NewsDetectiveView';
import { AudioMysteryView } from './AudioMysteryView';
import { WordTennisView } from './WordTennisView';
import { GameOverLeaderboard } from './GameOverLeaderboard';
import { useAuth } from '../../../contexts/AuthContext';
import { SessionStudyMaterial, SessionSavedItem } from '../../../services/friendSessionService';

interface SessionGameArenaProps {
  sessionId: string;
  game: SessionGameState;
  material: SessionStudyMaterial | null;
  savedItems: SessionSavedItem[];
  hostId: string;
  friendId: string;
  hostName: string;
  friendName: string;
  onClose: () => void;
}

export const SessionGameArena: React.FC<SessionGameArenaProps> = ({
  sessionId,
  game,
  material,
  savedItems,
  hostId,
  friendId,
  hostName,
  friendName,
  onClose
}) => {
  const { currentUser } = useAuth();
  const currentUid = currentUser?.uid || '';

  const myPlayer: SessionPlayerState = game.players[currentUid] || {
    userId: currentUid,
    userName: currentUser?.displayName || 'You',
    score: 0,
    isReady: true,
    roundFinished: false
  };

  const partnerUid = Object.keys(game.players).find((id) => id !== currentUid) || '';
  const partnerPlayer: SessionPlayerState | undefined = game.players[partnerUid];

  const currentRound = game.rounds[game.currentRoundIndex] || game.rounds[0];
  const isRoundResult = game.status === 'round_result';
  const isGameOver = game.status === 'game_over';

  const gameInfo = GAME_OPTIONS.find((g) => g.type === game.type);

  const handleSubmitAction = async (payload: any) => {
    try {
      await submitPlayerAnswer(sessionId, currentUid, payload);
    } catch (err) {
      console.error('Error submitting player answer:', err);
    }
  };

  const handleNextRound = async () => {
    try {
      await advanceSessionRound(sessionId);
    } catch (err) {
      console.error('Error advancing session round:', err);
    }
  };

  const handleLeaveGame = async () => {
    if (window.confirm('Are you sure you want to exit this game? Points earned so far will be saved.')) {
      try {
        await cancelOrEndSessionGame(sessionId);
        onClose();
      } catch (err) {
        console.error('Error leaving game:', err);
      }
    }
  };

  const handleRematch = async () => {
    try {
      await startSessionGame(
        sessionId,
        game.type,
        game.contentSource,
        material,
        savedItems,
        { id: hostId, name: hostName },
        { id: friendId, name: friendName }
      );
    } catch (err) {
      console.error('Error starting rematch:', err);
    }
  };

  const renderActiveGameView = () => {
    if (!currentRound) return null;

    const commonProps = {
      round: currentRound,
      myPlayer,
      partnerPlayer,
      onSubmit: handleSubmitAction,
      isRoundResult
    };

    switch (game.type) {
      case 'pronunciation_duel':
        return <PronunciationDuelView {...commonProps} />;
      case 'sentence_architect':
        return <SentenceArchitectView {...commonProps} />;
      case 'translation_duel':
        return <TranslationDuelView {...commonProps} />;
      case 'news_detective':
        return <NewsDetectiveView {...commonProps} />;
      case 'audio_mystery':
        return <AudioMysteryView {...commonProps} />;
      case 'word_tennis':
        return <WordTennisView {...commonProps} />;
      default:
        return <SentenceArchitectView {...commonProps} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-4xl max-h-[94vh] flex flex-col overflow-hidden">
        {/* Arena Top Bar */}
        <div className="p-4 md:px-6 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          {/* Game Title & Round Badge */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-600 text-white flex items-center justify-center shadow-xs">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 text-sm md:text-base leading-tight">
                  {gameInfo?.title || 'Learning Arena'}
                </h3>
                <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  Round {game.currentRoundIndex + 1} / {game.totalRounds}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                Playing with {partnerPlayer?.userName || 'Friend'}
              </p>
            </div>
          </div>

          {/* Scores Ticker */}
          <div className="flex items-center gap-2 md:gap-4 bg-white px-3 py-1.5 rounded-2xl border border-gray-200 shadow-xs">
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block">
                You
              </span>
              <span className="text-sm md:text-base font-black text-purple-600">
                {myPlayer.score} pts
              </span>
            </div>

            <span className="text-xs font-bold text-gray-300">vs</span>

            <div className="text-left">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block truncate max-w-[60px]">
                {partnerPlayer?.userName || 'Friend'}
              </span>
              <span className="text-sm md:text-base font-black text-gray-700">
                {partnerPlayer?.score || 0} pts
              </span>
            </div>
          </div>

          {/* Close Game Button */}
          <button
            onClick={handleLeaveGame}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            title="Leave Game"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Arena Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-b from-gray-50/50 to-white">
          {isGameOver ? (
            <GameOverLeaderboard
              game={game}
              currentUserId={currentUid}
              onRematch={handleRematch}
              onClose={async () => {
                await cancelOrEndSessionGame(sessionId);
                onClose();
              }}
            />
          ) : (
            renderActiveGameView()
          )}
        </div>

        {/* Round Result Navigation Banner */}
        {isRoundResult && !isGameOver && (
          <div className="bg-purple-600 text-white px-5 py-3 shadow-md flex items-center justify-between gap-3 animate-in fade-in">
            <div className="flex items-center gap-2 text-xs md:text-sm font-bold">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span>Round {game.currentRoundIndex + 1} Complete! Both friends submitted.</span>
            </div>

            <button
              onClick={handleNextRound}
              className="px-5 py-2 rounded-xl bg-white text-purple-900 font-black text-xs shadow-md hover:bg-purple-50 transition-all flex items-center gap-1.5"
            >
              <span>{game.currentRoundIndex + 1 >= game.totalRounds ? 'See Final Results' : 'Next Round'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
